/** @jsxImportSource ./simboluri-jsx */
import { useState } from "react";
import { PAL_LAYERE } from "./paletaDesen";
import { ArhitecturaResult, StepState, fmtDate } from "./tipuri";
import { stepDrawingUrl } from "./apiStructura";
import { viewerHash } from "./legaturi";
import { fileDownloadUrl } from "./apiStructura";
import { useFileIdByName } from "./useFileIds";
import { ArhitecturaElementArh } from "./tipuri";
import { DrawingView } from "./DwgViewer";
import Info from "./Info";
import { DescriereaPasului } from "./TitluPas";

import DeciziiPas from "./DeciziiPas";
import { t, tx, useT } from "../i18n";
// Secțiunile distincte ale stâlpilor desenați de arhitect, ca „40×40 cm".
function sectiuniDistincte(el: ArhitecturaElementArh[]): string {
  const set = new Set(
    el.map((e) => `${Math.round(e.b_m * 100)}×${Math.round(e.h_m * 100)} cm`),
  );
  return [...set].join(", ");
}

// Lățimile distincte ale grinzilor desenate de arhitect, ca „30 cm".
function latimiDistincte(el: ArhitecturaElementArh[]): string {
  const set = new Set(el.map((e) => `${Math.round(e.b_m * 100)} cm`));
  return [...set].join(", ");
}

// Sursa unei cifre: numele fișierului DWG + ochiul care deschide desenul și
// linkul de descărcare. Orice valoare citită dintr-un desen se afișează cu el,
// ca să poată fi verificată la sursă.
function SursaDwg({
  fisier,
  projectId,
}: {
  fisier?: string | null;
  // the old numeric structure-side id is still in the result JSON but the
  // platform addresses files by UUID — resolved from the name (useFileIds)
  fisierId?: number | null;
  projectId: string;
}) {
  useT();
  const fileId = useFileIdByName(projectId)(fisier);
  if (!fisier) return <span className="sg-muted">{t("struct.arh.sursa_necunoscuta")}</span>;
  return (
    <span className="st-sursa">
      <span className="sg-mono">{fisier}</span>
      {fileId && (
        <a
          className="sg-btnlink"
          href={viewerHash(projectId, fileId)}
          title={t("struct.arh.deschide_desenul", { fisier })}
        >
          {t("struct.arh.vezi_desenul")}
        </a>
      )}
      {fileId && (
        <a className="sg-btnlink" href={fileDownloadUrl(projectId, fileId)} title={t("struct.arh.descarca_dwg")}>
          DWG
        </a>
      )}
    </span>
  );
}

// Pasul 2 — Analiza arhitecturii: layerele relevante per nivel (pereți,
// goluri, etichete de spații), pereții simplificați ca dreptunghiuri
// (criteriu: ≥90% din punctele layerului de pereți acoperite) și
// înălțimile de etaj citite din cotele textuale ale fațadelor.

// JURNALUL EXTRACȚIEI — nu „Avertismente".
//
// Întrebarea lui Stefan: «ori pică verificarea și o vedem picată, ori trece —
// atunci ce e cu avertismentele?». La pasul ăsta răspunsul e că niciuna dintre
// notele de aici nu e o verificare: Pasul 2 nu are criterii de trecere, el
// CITEȘTE planurile. Ce spun notele e de unde vine fiecare cifră — pe ce layer
// s-a găsit structura, cu cât s-a închis o amprentă întreruptă de uși, ce
// categorie de încărcare s-a presupus în lipsa etichetelor de spații. Aia e
// PROVENIENȚĂ, și se citește pe nivelul la care s-a întâmplat, nu într-o listă
// plată de 21 de rânduri la coada paginii.
//
// Gruparea se face pe numele de nivel REALE (`r.niveluri`), nu ghicind un
// prefix: notele pasului sunt scrise ca „etaj 1: …", iar numele vin din
// aceeași sursă, deci potrivirea e exactă. Ce nu începe cu un nume de nivel e
// despre toată clădirea și rămâne împreună, la început.
function JurnalExtractie({
  note,
  niveluri,
}: {
  note: string[];
  niveluri: string[];
}) {
  useT();
  if (!note.length) return null;
  const global: string[] = [];
  const peNivel = new Map<string, string[]>();
  for (const n of note) {
    const nivel = niveluri.find((lv) => lv && n.startsWith(`${lv}: `));
    if (nivel) {
      if (!peNivel.has(nivel)) peNivel.set(nivel, []);
      peNivel.get(nivel)!.push(n.slice(nivel.length + 2));
    } else {
      global.push(n);
    }
  }
  return (
    <div className="st-findings">
      <h3>{t("struct.arh.jurnalul_extractiei", { n: note.length })}</h3>
      <div className="sg-muted" style={{ marginBottom: 6 }}>
        {t("struct.arh.unde_vine_cifra")}
      </div>
      {global.length > 0 && (
        <table className="sg-table" style={{ marginBottom: 8 }}>
          <tbody>
            {global.map((a, i) => (
              <tr key={i}>
                <td style={{ width: 120 }} className="sg-muted">
                  {t("struct.arh.toata_cladirea")}
                </td>
                <td>{a}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {peNivel.size > 0 && (
        <table className="sg-table">
          <tbody>
            {[...peNivel.entries()].map(([nivel, ale]) => (
              <tr key={nivel}>
                <td style={{ width: 120 }} className="sg-mono">
                  {nivel}
                </td>
                <td>
                  {ale.map((a, i) => (
                    // «MODIFICARE» = Pasul 2 a schimbat planșa (zidul etajului
                    // retras mutat pe axa structurii), nu doar a citit-o:
                    // se vede ca avertisment, ca să ajungă la arhitect
                    <div
                      key={i}
                      className={a.startsWith("MODIFICARE") ? "st-warn" : undefined}
                      style={{ marginBottom: 2 }}
                    >
                      {a}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function StepArhitectura({
  projectId,
  cloneId = null,
  state,
  running,
}: {
  projectId: string;
  // clona soluției vizualizate din care se citește desenul; null = proiectul
  cloneId?: string | null;
  state: StepState | null;
  running: boolean;
}) {
  useT();
  const [nivel, setNivel] = useState(0);

  if (running) {
    return (
      <div className="st-status">
        <div className="st-spinner" />
        <div>
          <strong>{t("struct.arh.pasul_ruleaza")}</strong>
          <div className="sg-muted">
            {t("struct.arh.identificarea_nivelurilo")}
          </div>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="sg-muted st-empty">
        {t("struct.pas.nerulat")}
        {/* descrierea vine din catalog_pasi.py — același text cu
            cel de la hover pe titlul pasului */}
        <DescriereaPasului cheie="analiza_arhitectura" />
      </div>
    );
  }

  const r = state.result as unknown as ArhitecturaResult;

  if (state.status === "error") {
    return (
      <div>
        <div className="sg-error">
          {t("struct.pas.esuat", { eroare: r?.error ?? t("struct.pas.eroare_necunoscuta") })}
        </div>
        <div className="sg-muted">{t("struct.arh.ruleaza_nou_meniul")}</div>
      </div>
    );
  }

  const ni = Math.min(nivel, r.niveluri.length - 1);
  const et = r.niveluri[ni];

  return (
    <div className="st-result">
      <div className="st-cards">
        <div className="st-card st-card-main">
          <div className="st-card-label">{t("struct.arh.regim_inaltime")}</div>
          <div className="st-card-value">{r.regim_inaltime || "?"}</div>
          <div className="st-card-sub">
            {r.nr_niveluri != null ? t("struct.arh.n_niveluri", { n: r.nr_niveluri }) : ""}
            {r.motiv}
          </div>
        </div>
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.arh.inaltimi_de_etaj")}
            <Info
              titlu={t("struct.arh.unde_vin_inaltimile")}
              ce={t("struct.arh.fatadele_au_scrise")}
              deCe={t("struct.arh.inaltimea_etaj_intra")}
              parametri={[
                { simbol: t("viz.cifre.cota"), nume: t("struct.arh.inaltimea_unui_nivel"), sursa: t("struct.arh.textele_fatade_citite") },
                { simbol: "h_etaj", nume: t("struct.arh.inaltimea_etajului_difer"), sursa: t("struct.arh.calculata_aici_folosita") },
                { simbol: "3,2 m", nume: t("struct.arh.inaltimea_rezerva_cand"), sursa: t("struct.arh.valoare_implicita_apare") },
              ]}
            />
          </div>
          <div className="st-card-value">
            {r.niveluri.map((n) => `${n.inaltime_m}`).join(" / ")} m
          </div>
          <div className="st-card-sub">
            {r.inaltimi?.fisier ? (
              <>
                {t("struct.arh.din_cotele_fatadei")}{" "}
                <SursaDwg
                  fisier={r.inaltimi.fisier}
                  fisierId={r.inaltimi.fisier_id}
                  projectId={projectId}
                />
              </>
            ) : (
              t("struct.arh.am_gasit_cote", { p1: r.inaltime_implicita_m })
            )}
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">
            {t("struct.arh.pereti_simplificati_card")}
            <Info
              titlu={t("struct.arh.peretii_simplificati")}
              ce={t("struct.arh.peretii_plan_redusi")}
              deCe={t("struct.arh.analiza_structurala_are")}
              parametri={[
                { simbol: "≥90%", nume: t("struct.arh.acoperirea_punctelor_lay"), sursa: t("struct.arh.criteriul_acceptare_extr") },
                { simbol: "±10°", nume: t("struct.arh.toleranta_unirea_bucatil"), sursa: t("struct.arh.unghiuri_180_acelasi") },
              ]}
            />
          </div>
          <div className="st-card-value">
            {r.niveluri.reduce((s, n) => s + n.nr_dreptunghiuri, 0)}
          </div>
          <div className="st-card-sub">
            {t("struct.arh.dreptunghiuri_pe_niveluri", { n: r.niveluri.length })}
            {r.pereti_persistenti
              ? t("struct.arh.p1_pereti_persistenti", { p1: r.pereti_persistenti.length })
              : ""}
          </div>
        </div>
        {/* GOLURILE și ZONELE EXTERIOARE — layere noi. Până
            acum Pasul 2 nu spunea nimic despre uși, ferestre și terase, deși
            tocmai ele decid pe unde poate trece o grindă și unde NU trebuie
            pus un stâlp. */}
        {r.niveluri.some((n) => n.goluri?.length) && (
          <div className="st-card">
            <div className="st-card-label">
              {t("struct.arh.goluri_usi_ferestre")}
              <Info
                titlu={t("struct.arh.golurile_fasii_coliniare")}
                ce={t("struct.arh.usa_fereastra_devine")}
                deCe={t("struct.arh.peste_gol_poate")}
              />
            </div>
            <div className="st-card-value">
              {r.niveluri.reduce((s, n) => s + (n.goluri?.length ?? 0), 0)}
            </div>
            <div className="st-card-sub">
              {t("struct.arh.usi_ferestre_gazda", {
                usi: r.niveluri.reduce(
                  (s, n) => s + (n.goluri?.filter((g) => g.tip === "usa").length ?? 0), 0),
                ferestre: r.niveluri.reduce(
                  (s, n) => s + (n.goluri?.filter((g) => g.tip === "fereastra").length ?? 0), 0),
                gazda: r.niveluri.reduce(
                  (s, n) => s + (n.goluri?.filter((g) => g.perete).length ?? 0), 0),
              })}
            </div>
          </div>
        )}
        {r.niveluri.some((n) => n.zone_exterioare?.length) && (
          <div className="st-card">
            <div className="st-card-label">
              {t("struct.arh.terase_balcoane_logii")}
              <Info
                titlu={t("struct.arh.zonele_afara")}
                ce={t("struct.arh.terasele_balcoanele_logi")}
                deCe={t("struct.arh.suprafata_construita_dec")}
              />
            </div>
            <div className="st-card-value">
              {r.niveluri.reduce((s, n) => s + (n.zone_exterioare?.length ?? 0), 0)}
            </div>
            <div className="st-card-sub">
              {t("struct.arh.m2_in_total", {
                arie: r.niveluri
                  .flatMap((n) => n.zone_exterioare ?? [])
                  .reduce((s, z) => s + (z.arie_m2 ?? 0), 0)
                  .toFixed(1),
              })}
            </div>
          </div>
        )}
      </div>

      <DeciziiPas decizii={r.decizii} titlu="struct.decizii.pas2" />
      <JurnalExtractie
        note={r.avertismente ?? []}
        niveluri={(r.niveluri ?? []).map((n) => n.nivel)}
      />

      {r.inaltimi?.cote && r.inaltimi.cote.length > 0 ? (
        <div className="st-findings">
          <h3>
            {t("struct.arh.cotele_de_nivel_gasite")}
            <Info
              titlu={t("struct.arh.cotele_unde_luate")}
              ce={t("struct.arh.rand_text_existent")}
              deCe={t("struct.arh.cota_valoare_citita")}
              parametri={[
                { simbol: t("struct.top.nivel"), nume: t("struct.arh.nivelul_refera_cota"), sursa: t("struct.arh.potrivit_llm_numele") },
                { simbol: t("struct.arh.cota"), nume: t("struct.arh.inaltimea_fata_reperul"), sursa: t("struct.arh.numarul_textul_fatada") },
                { simbol: t("struct.arh.text_sursa"), nume: t("struct.arh.textul_exact_dwg"), sursa: t("struct.arh.entitatea_tip_text") },
              ]}
            />
          </h3>
          <div className="sg-muted st-sursa-linie">
            {t("struct.arh.sursa_doua_puncte")}{" "}
            <SursaDwg
              fisier={r.inaltimi.fisier}
              fisierId={r.inaltimi.fisier_id}
              projectId={projectId}
            />
          </div>
          <table className="sg-table">
            <thead>
              <tr><th>{t("struct.top.nivel")}</th><th>{t("struct.arh.cota")}</th><th>{t("struct.arh.text_sursa")}</th><th>{t("struct.arh.sursa")}</th></tr>
            </thead>
            <tbody>
              {r.inaltimi.cote.map((c, i) => (
                <tr key={i}>
                  <td><strong>{c.nivel}</strong></td>
                  <td className="sg-mono">{c.cota_m > 0 ? `+${c.cota_m}` : c.cota_m} m</td>
                  <td className="sg-mono">{c.text_sursa}</td>
                  <td className="sg-mono">
                    {c.fisier ?? r.inaltimi.fisier ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="sg-muted">{r.inaltimi.motiv}</div>
          {r.inaltimi.incercate && r.inaltimi.incercate.length > 1 && (
            <div className="sg-muted" style={{ marginTop: 6 }}>
              {t("struct.arh.fatade_incercate")}{" "}
              {r.inaltimi.incercate
                .map((t) => `${t.fisier} (${t.rezultat})`)
                .join(" · ")}
            </div>
          )}
        </div>
      ) : (
        <div className="st-findings">
          <h3>{t("struct.arh.cotele_nivel_s")}</h3>
          <div className="sg-muted">
            {t("struct.arh.nicio_fatada", { h: r.inaltime_implicita_m })}
          </div>
          {r.inaltimi?.incercate && r.inaltimi.incercate.length > 0 ? (
            <table className="sg-table" style={{ marginTop: 8 }}>
              <thead>
                <tr><th>{t("struct.arh.fatada_incercata")}</th><th>{t("struct.rzv.rezultat")}</th><th /></tr>
              </thead>
              <tbody>
                {r.inaltimi.incercate.map((t, i) => (
                  <tr key={i}>
                    <td className="sg-mono">{t.fisier}</td>
                    <td>{t.rezultat}</td>
                    <td className="sg-actions">
                      <SursaDwg
                        fisier={t.fisier}
                        fisierId={t.fisier_id}
                        projectId={projectId}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="sg-muted" style={{ marginTop: 6 }}>
              {t("struct.arh.fisiere_fatade")}{" "}
              {r.fatade.length > 0
                ? r.fatade
                    .map((f) => (typeof f === "string" ? f : f.name))
                    .join(", ")
                : t("struct.tipver.niciunul")}
            </div>
          )}
        </div>
      )}

      <div className="st-findings">
        <h3>
          {t("struct.arh.nivelurile")}
          <Info
            titlu={t("struct.arh.inseamna_coloana")}
            ce={t("struct.arh.datele_masurate_nivel")}
            parametri={[
              { simbol: t("struct.arh.simbol_plan_sursa"), nume: t("struct.arh.fisierul_dwg_s"), sursa: t("struct.arh.planurile_arhitectura_in") },
              { simbol: t("struct.arh.simbol_etaj_repetat"), nume: t("struct.arh.nivelul_foloseste_planul"), sursa: t("struct.arh.titlul_planului_plan") },
              { simbol: t("struct.arh.simbol_unitatea_desenului"), nume: t("struct.arh.cati_milimetri_are"), sursa: t("struct.arh.dedusa_inaltimea_textelo") },
              { simbol: t("struct.arh.inaltime"), nume: t("struct.arh.inaltimea_etajului_dista"), sursa: t("struct.arh.diferenta_cotelor_fatade") },
              { simbol: t("insp.rand.arie"), nume: t("struct.arh.suprafata_construita_a"), sursa: t("struct.arh.masurata_aici_folosita") },
              { simbol: t("struct.arh.acoperire_puncte"), nume: t("struct.arh.criteriul_calitate_proce"), sursa: t("struct.arh.masurat_aici_sub") },
              { simbol: t("struct.arh.dreptunghiuri"), nume: t("struct.arh.cati_pereti_simplificati"), sursa: t("struct.arh.extractia_pas") },
              { simbol: t("struct.arh.lungime_totala"), nume: t("struct.arh.suma_lungimilor_peretilo"), sursa: t("struct.arh.calculata_dreptunghiuri") },
            ]}
          />
        </h3>
        <table className="sg-table">
          <thead>
            <tr>
              <th>{t("struct.top.nivel")}</th>
              <th>{t("struct.arh.plan_sursa_dwg")}</th>
              <th>{t("struct.arh.inaltime")}</th>
              <th>{t("insp.rand.arie")}</th>
              <th>{t("struct.arh.acoperire_puncte")}</th>
              <th>{t("struct.arh.dreptunghiuri")}</th>
              <th>{t("struct.arh.lungime_totala")}</th>
            </tr>
          </thead>
          <tbody>
            {r.niveluri.map((n, i) => (
              <tr key={i}>
                <td><strong>{n.nivel}</strong></td>
                <td>
                  <SursaDwg
                    fisier={n.fisier}
                    fisierId={n.fisier_id}
                    projectId={projectId}
                  />
                  {n.repetat && (
                    <div className="st-sursa-nota">
                      {t("struct.arh.etaj_repetat_nota")}
                      {n.repetare_sursa ? ` (${n.repetare_sursa})` : ""}
                    </div>
                  )}
                  {n.unitate_factor_mm != null && n.unitate_factor_mm !== 1 && (
                    <div className="st-sursa-nota">
                      {t(n.unitate_factor_mm === 1000 ? "struct.arh.desen_in_metri" : "struct.arh.desen_in_centimetri",
                        { f: n.unitate_factor_mm })}
                    </div>
                  )}
                  {/* pe o planșă cu mai multe desene alăturate: care dintre
                      ele e planul nivelului și cum a fost recunoscut */}
                  {n.separare_plan && n.separare_plan.nr_candidati > 1 && (
                    <div className="st-sursa-nota">
                      {t("struct.arh.separare_ales", { n: n.separare_plan.nr_candidati })}
                      {n.separare_plan.dimensiuni_m
                        ? " " + t("struct.arh.separare_cel_de", {
                            a: n.separare_plan.dimensiuni_m[0],
                            b: n.separare_plan.dimensiuni_m[1],
                          })
                        : ""}
                      {" — "}{n.separare_plan.metoda}
                      {n.separare_plan.motiv ? `: ${n.separare_plan.motiv}` : ""}
                    </div>
                  )}
                  {/* deplasarea cu care planul a fost suprapus peste celelalte
                      niveluri; suprapunerea = cât din pereții lui cad peste
                      pereții nivelului de referință */}
                  {n.incadrare &&
                    (n.incadrare.dx_mm !== 0 || n.incadrare.dy_mm !== 0) && (
                      <div className="st-sursa-nota">
                        {t("struct.arh.aliniat_peste", {
                          dx: (n.incadrare.dx_mm / 1000).toFixed(2),
                          dy: (n.incadrare.dy_mm / 1000).toFixed(2),
                        })}
                        {n.incadrare.suprapunere != null
                          ? t("struct.arh.peretii_suprapun_p1", { p1: Math.round(
                              n.incadrare.suprapunere * 100,
                            ) })
                          : ""}
                      </div>
                    )}
                </td>
                <td className="sg-mono">
                  {n.inaltime_m} m
                  {n.inaltime_sursa && (
                    <div className="st-sursa-nota">
                      {n.inaltime_sursa.formula}
                      {n.inaltime_sursa.fisier
                        ? t("struct.arh.p1", { p1: n.inaltime_sursa.fisier })
                        : ""}
                    </div>
                  )}
                </td>
                <td className="sg-mono">
                  {n.arie_m2 != null ? `${n.arie_m2} m²` : "—"}
                  {/* de unde vine cifra: conturul desenat se închide singur
                      sau a trebuit închis peste ușile fără geometrie */}
                  {n.amprenta && (
                    <div className="st-sursa-nota">
                      {n.amprenta.contur_rupt
                        ? t("struct.arh.contur_rupt_nici", { p1: (
                            n.amprenta.r_inchidere_mm / 1000
                          ).toFixed(1), p2: n.amprenta.control_m2 ?? "?" })
                        : n.amprenta.r_inchidere_mm
                          ? t("struct.arh.pereti_desenati_intrerup", { p1: (
                              n.amprenta.r_inchidere_mm / 1000
                            ).toFixed(1), p2: n.amprenta.arie_fara_inchidere_m2 })
                          : t("struct.arh.conturul_se_inchide")}
                    </div>
                  )}
                  {!!n.placi_consola?.length && (
                    <div className="st-sursa-nota">
                      {t("struct.arh.inclusiv_placi_consola", {
                        n: n.placi_consola.length,
                        arie: n.placi_consola
                          .reduce((s, p) => s + p.arie_m2, 0)
                          .toFixed(1),
                      })}
                    </div>
                  )}
                  {!!n.zone_exterioare?.length && (
                    <div className="st-sursa-nota">
                      {t("struct.arh.zone_exterioare", { n: n.zone_exterioare.length })}{" "}
                      {n.zone_exterioare
                        .map((z) =>
                          `${z.id} ${z.tip}${z.arie_m2 ? ` ${z.arie_m2} m²` : ""}`)
                        .join(" · ")}
                    </div>
                  )}
                  {!!n.goluri?.length && (
                    <div className="st-sursa-nota">
                      {t("struct.arh.goluri_nivel", {
                        n: n.goluri.length,
                        usi: n.goluri.filter((g) => g.tip === "usa").length,
                        ferestre: n.goluri.filter((g) => g.tip === "fereastra").length,
                        min: Math.min(...n.goluri.map((g) => g.lungime_m)).toFixed(2),
                        max: Math.max(...n.goluri.map((g) => g.lungime_m)).toFixed(2),
                      })}
                    </div>
                  )}
                </td>
                <td className="sg-mono">
                  {n.acoperire_puncte_pct != null ? `${n.acoperire_puncte_pct}%` : "—"}
                </td>
                <td className="sg-mono">{n.nr_dreptunghiuri}</td>
                <td className="sg-mono">
                  {n.dreptunghiuri
                    .reduce((s, d) => s + d.lungime_m, 0)
                    .toFixed(1)}{" "}
                  m
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {r.niveluri.some((n) => n.functiuni) && (
        <div className="st-findings">
          <h3>
            {t("struct.arh.functiunile_spatiilor")}
            <Info
              titlu={t("struct.arh.a_vs_c")}
              ce={t("struct.arh.etichetele_spatiilor_pla")}
              deCe={t("struct.arh.pasul_8_aplica")}
              parametri={[
                { simbol: "A", nume: t("struct.arh.locuit_q"), sursa: t("struct.arh.clasificarea_llm_a") },
                { simbol: "C", nume: t("struct.arh.circulatii_comune_balcoa"), sursa: t("struct.arh.idem_zona_camera") },
                { simbol: t("struct.arh.simbol_conturul_zonei"), nume: t("struct.arh.poligonul_desenat_diagra"), sursa: t("struct.arh.camera_ingradita_intre") },
              ]}
            />
          </h3>
          <div className="sg-muted" style={{ marginBottom: 8 }}>
            {tx("struct.arh.conturul_zonei_urmeaza", { pereti: t("struct.arh.peretii_simplificati2") })}
          </div>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.top.nivel")}</th>
                <th>{t("struct.arh.tip_cladire")}</th>
                <th>{t("struct.arh.arie_a")}</th>
                <th>{t("struct.inc.col_arie_c")}</th>
                <th>{t("struct.arh.zonele_c")}</th>
              </tr>
            </thead>
            <tbody>
              {r.niveluri.map((n, i) => {
                const fx = n.functiuni;
                if (!fx) return null;
                const zoneC = fx.zone.filter((z) => z.functiune === "C");
                return (
                  <tr key={i}>
                    <td><strong>{n.nivel}</strong></td>
                    <td>
                      {fx.tip_cladire}
                      <div className="sg-muted" style={{ fontSize: 11 }}>{fx.motiv}</div>
                    </td>
                    <td className="sg-mono">{fx.arie_a_m2} m²</td>
                    <td className="sg-mono">{fx.arie_c_m2} m²</td>
                    <td className="sg-muted">
                      {zoneC.length > 0
                        ? zoneC.map((z) => `${z.eticheta} (${z.arie_m2} m²)`).join(", ")
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="st-findings">
        <h3>{t("struct.arh.diagrama_per_nivel")}</h3>
        <div className="md-tabs">
          {r.niveluri.map((n, i) => (
            <button
              key={i}
              className={`md-tab ${i === ni ? "active" : ""}`}
              onClick={() => setNivel(i)}
            >
              {n.nivel}
            </button>
          ))}
        </div>
        <div className="md-legend">
          <span>
            <span className="md-swatch" style={{ background: PAL_LAYERE.peretiSimplificati }} />{" "}
            {t("struct.arh.leg_pereti_simplificati")}
          </span>
          <span>
            <span className="md-swatch" style={{ background: PAL_LAYERE.peretiPersistenti }} />{" "}
            {t("struct.arh.leg_pereti_persistenti")}
          </span>
          {!!et.placi_consola?.length && (
            <span>
              <span className="md-swatch" style={{ background: PAL_LAYERE.consola }} />{" "}
              {t("struct.arh.leg_placi_consola")}
            </span>
          )}
          <span className="sg-muted">
            {t("struct.arh.leg_layere_aprinse")}
          </span>
          <span className="sg-muted">
            {t("struct.arh.leg_click_perete")}
          </span>
        </div>
        <div className="st-finding">
          <span className="st-finding-label">{t("struct.top.nivel")}</span>
          <div>
            <strong>{et.nivel}</strong> — <span className="sg-mono">{et.fisier}</span>
            <div className="sg-muted">
              {t("struct.arh.lay_pereti")} {et.layere_pereti.map((l) => <code key={l} className="st-chip">{l}</code>)}
              {et.layere_goluri.length > 0 && (
                <> {t("struct.arh.lay_goluri")} {et.layere_goluri.map((l) => <code key={l} className="st-chip">{l}</code>)}</>
              )}
              {et.layere_etichete.length > 0 && (
                <> {t("struct.arh.lay_etichete")} {et.layere_etichete.map((l) => <code key={l} className="st-chip">{l}</code>)}</>
              )}
              {!!et.layere_balcoane?.length && (
                <> {t("struct.arh.lay_balcoane")} {et.layere_balcoane.map((l) => <code key={l} className="st-chip">{l}</code>)}</>
              )}
              {!!et.layere_structura_arhitect?.length && (
                <> {t("struct.arh.lay_structura_arhitect")} {et.layere_structura_arhitect.map((l) => <code key={l} className="st-chip">{l}</code>)}</>
              )}
            </div>
            <div className="sg-muted">{et.motiv_layere}</div>
            {/* ce a desenat deja arhitectul: REPER de comparație, nu sursă
                de propunere — diafragmele se numără separat de grinzi */}
            {!!(et.structura_arhitect &&
              (et.structura_arhitect.stalpi.length ||
                (et.structura_arhitect.diafragme?.length ?? 0) ||
                et.structura_arhitect.grinzi.length)) && (
              <div className="sg-muted">
                {tx("struct.arh.arhitectul_a_desenat", {
                  st: et.structura_arhitect.stalpi.length,
                  sst: et.structura_arhitect.stalpi.length > 0
                    ? ` (${sectiuniDistincte(et.structura_arhitect.stalpi)})`
                    : "",
                  d: et.structura_arhitect.diafragme?.length ?? 0,
                  sd: (et.structura_arhitect.diafragme?.length ?? 0) > 0
                    ? ` (${sectiuniDistincte(et.structura_arhitect.diafragme!)})`
                    : "",
                  g: et.structura_arhitect.grinzi.length,
                  sg: et.structura_arhitect.grinzi.length > 0
                    ? " " + t("struct.arh.latimi", { l: latimiDistincte(et.structura_arhitect.grinzi) })
                    : "",
                })}
              </div>
            )}
          </div>
        </div>
        <div className="sg-muted st-ran-at">{t("struct.pas.rulat_la", { cand: fmtDate(state.updated_at) })}</div>
      </div>

      <div className="st-preview">
        <DrawingView
          key={ni}
          src={stepDrawingUrl(projectId, "analiza_arhitectura", ni, cloneId)}
          title={t("struct.arh.titlu_desen", { nivel: et.nivel })}
          embed
        />
      </div>
    </div>
  );
}
